import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { AuthError } from 'aws-amplify/auth';
import { z } from 'zod';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';

import { Button } from '@shieldpay/bumblebee/button';
import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import { Infoblock } from '@shieldpay/bumblebee/infoblock';
import { InputField } from '@shieldpay/bumblebee/input-field';
import { Layout } from '@shieldpay/bumblebee/layout';
import { Modal } from '@shieldpay/bumblebee/modal';
import { PasswordInput } from '@shieldpay/bumblebee/password-input';
import { Text } from '@shieldpay/bumblebee/text';

const FieldSchema = z.object({
  username: z
    .string({ required_error: 'login.validation.enterEmail' })
    .email({ message: 'login.validation.emailFormat' }),
  password: z.string({ required_error: 'login.validation.enterPassword' }),
});

export type LoginFormFields = z.infer<typeof FieldSchema>;

export interface LoginFormProps {
  submitting?: boolean;
  resetErrorState: () => void;
  errorState: AuthError['name'] | undefined;
  onSubmit: (data: LoginFormFields) => void;
}

export const LoginForm = ({
  onSubmit,
  submitting,
  errorState,
  resetErrorState,
}: LoginFormProps) => {
  const { formatMessage } = useIntl();

  const [form, field] = useForm({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) => {
      if (errorState) resetErrorState();

      return parseWithZod(formData, { schema: FieldSchema });
    },
    onSubmit: (e, { submission }) => {
      // Prevent the default form submission - auth is handled client-side
      e.preventDefault();

      onSubmit(FieldSchema.parse(submission?.payload));
    },
  });

  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);

  return (
    <Layout stack="column" spacing="basePos4">
      <Form
        {...getFormProps(form)}
        spacing="basePos3"
        title={formatMessage({ id: 'login.loginDetails' })}
      >
        <Heading>
          <FormattedMessage id="login.logInToAccount" />
        </Heading>
        <InputField
          label={<FormattedMessage id="shared.email" />}
          {...getInputProps(field.username, { type: 'text' })}
          errors={field.username.errors?.map((id) => formatMessage({ id }))}
        />
        <PasswordInput
          label={<FormattedMessage id="shared.password" />}
          {...getInputProps(field.password, { type: 'text' })}
          errors={field.password.errors?.map((id) => formatMessage({ id }))}
          hidePasswordText={<FormattedMessage id="login.hidePassword" />}
          showPasswordText={<FormattedMessage id="login.showPassword" />}
        />
        {errorState ? (
          <Infoblock variant="error">
            <FormattedMessage id="login.validation.invalid" />
          </Infoblock>
        ) : null}
        <Button type="submit" size="large" loading={submitting}>
          <FormattedMessage id="login.logIn" />
        </Button>
      </Form>
      <Text paragraph variant="body">
        <Button
          variant="text-underline"
          onClick={() => {
            setForgotPasswordModalOpen(true);
          }}
        >
          <FormattedMessage id="login.forgotPasswordLink" />
        </Button>
      </Text>

      <Modal
        dismissable
        open={forgotPasswordModalOpen}
        onClose={() => {
          setForgotPasswordModalOpen(false);
        }}
        closeDialogText={formatMessage({ id: 'shared.close' })}
      >
        <Modal.Title>
          <FormattedMessage id="login.forgotPasswordHeading" />
        </Modal.Title>
        <Text paragraph variant="body150">
          <FormattedMessage id="login.forgotPasswordInstructions" />
        </Text>
        <Modal.Actions>
          <Modal.CloseButton>
            <FormattedMessage id="shared.ok" />
          </Modal.CloseButton>
        </Modal.Actions>
      </Modal>
    </Layout>
  );
};
